import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../styles/components/date-range-picker.css';

const PRESETS = [
  { label: 'Today', getRange: () => { const d = new Date(); return [startOfDay(d), endOfDay(d)]; } },
  { label: 'Yesterday', getRange: () => { const d = new Date(); d.setDate(d.getDate() - 1); return [startOfDay(d), endOfDay(d)]; } },
  { label: 'Last 7 Days', getRange: () => { const end = new Date(); const start = new Date(); start.setDate(start.getDate() - 6); return [startOfDay(start), endOfDay(end)]; } },
  { label: 'Last 30 Days', getRange: () => { const end = new Date(); const start = new Date(); start.setDate(start.getDate() - 29); return [startOfDay(start), endOfDay(end)]; } },
  { label: 'Last 90 Days', getRange: () => { const end = new Date(); const start = new Date(); start.setDate(start.getDate() - 89); return [startOfDay(start), endOfDay(end)]; } },
  { label: 'This Month', getRange: () => { const now = new Date(); return [startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), endOfDay(now)]; } },
  { label: 'Last Month', getRange: () => { const now = new Date(); const start = new Date(now.getFullYear(), now.getMonth() - 1, 1); const end = new Date(now.getFullYear(), now.getMonth(), 0); return [startOfDay(start), endOfDay(end)]; } },
  { label: 'Custom Range', getRange: null },
];

function startOfDay(d) { const n = new Date(d); n.setHours(0, 0, 0, 0); return n; }
function endOfDay(d) { const n = new Date(d); n.setHours(23, 59, 59, 999); return n; }
function isSameDay(a, b) { return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function isInRange(day, start, end) {
  if (!start || !end) return false;
  const t = day.getTime();
  return t >= startOfDay(start).getTime() && t <= endOfDay(end).getTime();
}
function formatDisplay(d) {
  if (!d) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
}

function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfWeek(year, month) { return new Date(year, month, 1).getDay(); }

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * DateRangePicker — dual calendar with preset shortcuts.
 * Props:
 *   startDate: Date
 *   endDate: Date
 *   onChange: (start: Date, end: Date) => void
 */
const DateRangePicker = ({ startDate, endDate, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd, setDraftEnd] = useState(endDate);
  const [activePreset, setActivePreset] = useState('Last 7 Days');
  const [hoverDate, setHoverDate] = useState(null);

  // Left calendar shows previous month, right shows current month
  const now = new Date();
  const [rightMonth, setRightMonth] = useState(now.getMonth());
  const [rightYear, setRightYear] = useState(now.getFullYear());

  const wrapperRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const handlePresetClick = useCallback((preset) => {
    setActivePreset(preset.label);
    if (preset.getRange) {
      const [s, e] = preset.getRange();
      setDraftStart(s);
      setDraftEnd(e);
      // Adjust calendar view to show the end date's month
      setRightMonth(e.getMonth());
      setRightYear(e.getFullYear());
    }
  }, []);

  const handleDayClick = (day) => {
    setActivePreset('Custom Range');
    if (!draftStart || (draftStart && draftEnd)) {
      // Start new selection
      setDraftStart(day);
      setDraftEnd(null);
    } else {
      // Complete the selection
      if (day < draftStart) {
        setDraftEnd(draftStart);
        setDraftStart(day);
      } else {
        setDraftEnd(day);
      }
    }
  };

  const handleApply = () => {
    if (draftStart && draftEnd) {
      onChange(startOfDay(draftStart), endOfDay(draftEnd));
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setDraftStart(startDate);
    setDraftEnd(endDate);
    setIsOpen(false);
  };

  const navigateMonth = (delta) => {
    let m = rightMonth + delta;
    let y = rightYear;
    if (m > 11) { m = 0; y++; }
    if (m < 0) { m = 11; y--; }
    setRightMonth(m);
    setRightYear(y);
  };

  // Left calendar is one month before right
  let leftMonth = rightMonth - 1;
  let leftYear = rightYear;
  if (leftMonth < 0) { leftMonth = 11; leftYear--; }

  const today = new Date();

  const renderCalendar = (year, month) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfWeek(year, month);
    const cells = [];

    // Previous month padding
    const prevDays = getDaysInMonth(year, month - 1);
    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push({ day: prevDays - i, inMonth: false });
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, inMonth: true });
    }
    // Next month padding
    const remainder = cells.length % 7;
    if (remainder > 0) {
      for (let i = 1; i <= 7 - remainder; i++) {
        cells.push({ day: i, inMonth: false });
      }
    }

    return (
      <div className="drp-calendar">
        <div className="drp-calendar-header">
          <span className="drp-month-label">{MONTHS[month]} {year}</span>
        </div>
        <div className="drp-weekdays">
          {DAYS.map(d => <span key={d} className="drp-weekday">{d}</span>)}
        </div>
        <div className="drp-days">
          {cells.map((cell, idx) => {
            if (!cell.inMonth) {
              return <span key={idx} className="drp-day drp-day--outside">{cell.day}</span>;
            }
            const date = new Date(year, month, cell.day);
            const isToday = isSameDay(date, today);
            const isStart = isSameDay(date, draftStart);
            const isEnd = isSameDay(date, draftEnd);
            const effectiveEnd = draftEnd || hoverDate;
            const inRange = draftStart && effectiveEnd && isInRange(date, draftStart, effectiveEnd);
            const isFuture = date > today;

            let cls = 'drp-day';
            if (isStart || isEnd) cls += ' drp-day--selected';
            else if (inRange) cls += ' drp-day--in-range';
            if (isToday) cls += ' drp-day--today';
            if (isFuture) cls += ' drp-day--disabled';

            return (
              <span
                key={idx}
                className={cls}
                onClick={() => !isFuture && handleDayClick(date)}
                onMouseEnter={() => !isFuture && !draftEnd && draftStart && setHoverDate(date)}
                onMouseLeave={() => setHoverDate(null)}
              >
                {cell.day}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="drp-wrapper" ref={wrapperRef}>
      <button className="drp-trigger" onClick={() => setIsOpen(!isOpen)}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span>{formatDisplay(startDate)} – {formatDisplay(endDate)}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="drp-dropdown">
          <div className="drp-presets">
            {PRESETS.map(p => (
              <button
                key={p.label}
                className={`drp-preset-btn${activePreset === p.label ? ' drp-preset-btn--active' : ''}`}
                onClick={() => handlePresetClick(p)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="drp-calendars">
            <div className="drp-nav">
              <button className="drp-nav-btn" onClick={() => navigateMonth(-1)}>‹</button>
              <div className="drp-calendars-grid">
                {renderCalendar(leftYear, leftMonth)}
                {renderCalendar(rightYear, rightMonth)}
              </div>
              <button className="drp-nav-btn" onClick={() => navigateMonth(1)}>›</button>
            </div>
            <div className="drp-footer">
              <span className="drp-range-display">
                {formatDisplay(draftStart)} – {formatDisplay(draftEnd)}
              </span>
              <div className="drp-actions">
                <button className="drp-btn drp-btn--cancel" onClick={handleCancel}>Cancel</button>
                <button
                  className="drp-btn drp-btn--apply"
                  onClick={handleApply}
                  disabled={!draftStart || !draftEnd}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
